// Manage Layouts view for the Layout Config screen.
//
// Replaces the main inbox area when the user clicks the "Manage Layout"
// header button. Mirrors the reference's LayoutList
// (ocr-ui/src/modules/document-extraction/config-studio/rules-editor
// /LayoutList.jsx) — a paginated table with search + status filter and
// per-row edit / delete actions.
//
// Data sources:
//   POST /document_extraction/management_layout/layout_id_list
//   POST /document_extraction/management_layout/layout_id_delete

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Settings,
  Inbox,
  Search,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  Loader2,
  Plus,
  Layers,
} from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";
import type { LayoutSummary } from "../../../types/documents";
import { ManageLayoutEditView } from "./ManageLayoutEditView";

interface ManageLayoutsViewProps {
  /** Fires when the user clicks "Back to Template Engine" to dismiss the view. */
  onBack: () => void;
  /** Open the create-layout modal. */
  onCreate: () => void;
  /**
   * Open the edit-layout modal for a specific layout. Kept on the prop
   * surface for backwards-compat — the new inline ManageLayoutEditView
   * replaces this for in-Manage-Layouts edits, but other callers (and
   * the workflow popover) still reach in through here.
   */
  onEdit: (layoutId: string) => void;
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50] as const;

export function ManageLayoutsView({
  onBack,
  onCreate,
  onEdit,
}: ManageLayoutsViewProps) {
  // Silence the unused-arg warning while we keep the prop for parent
  // callers that still want to handle edit themselves. Inline edit
  // takes precedence — see the `editingLayoutId` state below.
  void onCreate;
  void onEdit;

  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  // When set, the editor view replaces the layouts table.
  const [editingLayoutId, setEditingLayoutId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["ocr", "layouts"],
    queryFn: () => ocrApi.listLayouts(),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const allLayouts: LayoutSummary[] = listQuery.data?.layouts ?? [];

  const filtered = useMemo<LayoutSummary[]>(() => {
    const q = search.trim().toLowerCase();
    return allLayouts.filter((l) => {
      const matchesSearch =
        !q ||
        [l.layout_id, l.workflow_id ?? "", l.customer_name ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(q);
      const layoutStatus = ((l as { status?: string }).status ?? "active").toLowerCase();
      const matchesStatus =
        statusFilter === "all" || layoutStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [allLayouts, search, statusFilter]);

  const totalRecords = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / rowsPerPage));
  const safePageNumber = Math.min(pageNumber, totalPages);
  const pageStart = (safePageNumber - 1) * rowsPerPage;
  const pageRows = filtered.slice(pageStart, pageStart + rowsPerPage);
  const showingFrom = totalRecords === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + rowsPerPage, totalRecords);

  const deleteMutation = useMutation({
    mutationFn: (layoutId: string) => ocrApi.deleteLayout(layoutId),
    onSuccess: (_, layoutId) => {
      toast.success(`Deleted layout "${layoutId}"`);
      queryClient.invalidateQueries({ queryKey: ["ocr", "layouts"] });
    },
    onError: (err: unknown) => {
      toast.error("Delete failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });

  const handleDelete = (layoutId: string) => {
    if (!window.confirm(`Delete layout "${layoutId}"? This cannot be undone.`)) {
      return;
    }
    deleteMutation.mutate(layoutId);
  };

  // Edit a specific layout — replaces the table with the full-page editor.
  if (editingLayoutId) {
    return (
      <ManageLayoutEditView
        layoutId={editingLayoutId}
        onBack={() => setEditingLayoutId(null)}
      />
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-auto bg-gray-50">
      {/* === Page header ============================================== */}
      <header className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-8 py-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-3 bg-blue-50 rounded-xl flex-shrink-0">
            <Settings size={22} className="text-blue-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">Manage Layout</h1>
            <p className="text-sm text-gray-500">
              Configure document extraction rules and layouts.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* <button
            type="button"
            onClick={onCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            <Plus size={16} />
            New layout
          </button> */}
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <ArrowLeft size={16} className="text-gray-500" />
            Back to Template Engine
          </button>
        </div>
      </header>

      {/* === Card with toolbar + table + pagination =================== */}
      <div className="flex-1 px-8 py-6">
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 px-6 py-4">
            <div className="flex items-center gap-2">
              <Inbox className="h-5 w-5 text-blue-500" />
              <h2 className="text-base font-semibold text-gray-900">
                Manage Layouts
              </h2>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums">
                {listQuery.isLoading ? "…" : totalRecords}
              </span>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPageNumber(1);
                  }}
                  placeholder="Search Layout…"
                  className="w-72 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as typeof statusFilter);
                  setPageNumber(1);
                }}
                className="min-w-[140px] cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-y border-gray-100 bg-gray-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-6 py-3">Layout ID</th>
                  <th className="px-6 py-3">Workflow ID</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listQuery.isLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-16 text-center text-sm text-gray-500"
                    >
                      Loading layouts…
                    </td>
                  </tr>
                ) : listQuery.isError ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-16 text-center">
                      <div className="mx-auto max-w-md rounded-md border border-rose-200 bg-rose-50 p-4 text-left text-rose-700">
                        <p className="font-medium">Couldn't load layouts.</p>
                        <p className="mt-1 text-rose-600">
                          {listQuery.error instanceof Error
                            ? listQuery.error.message
                            : "Unknown error"}
                        </p>
                        <button
                          type="button"
                          onClick={() => listQuery.refetch()}
                          className="mt-3 inline-flex items-center rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
                        >
                          Retry
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-20">
                      <div className="flex flex-col items-center text-center">
                        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                          <Layers className="h-7 w-7" />
                        </div>
                        <p className="text-base font-semibold text-gray-900">
                          No layouts found
                        </p>
                        <p className="mt-1 max-w-sm text-sm text-gray-500">
                          {search
                            ? `We couldn't find any layouts matching "${search}".`
                            : "Start by creating a layout to define document extraction rules."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => {
                    const layoutStatus = String(
                      (row as { status?: string }).status ?? "active",
                    );
                    return (
                      <tr key={row.layout_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">
                          {row.layout_id}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {row.workflow_id || "—"}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={layoutStatus} />
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => setEditingLayoutId(row.layout_id)}
                              className="rounded-md p-1.5 text-blue-500 hover:bg-blue-50 hover:text-blue-700"
                              title="Edit Layout"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row.layout_id)}
                              disabled={
                                deleteMutation.isPending &&
                                deleteMutation.variables === row.layout_id
                              }
                              className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
                              title="Delete Layout"
                            >
                              {deleteMutation.isPending &&
                              deleteMutation.variables === row.layout_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-3 text-sm text-gray-600">
            <div>
              Showing{" "}
              <span className="font-medium text-gray-900 tabular-nums">
                {showingFrom}
              </span>{" "}
              to{" "}
              <span className="font-medium text-gray-900 tabular-nums">
                {showingTo}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-900 tabular-nums">
                {totalRecords}
              </span>{" "}
              entries
            </div>
            <div className="flex items-center gap-3">
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPageNumber(1);
                }}
                className="cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {ROWS_PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} rows / page
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={safePageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </button>
                <button
                  type="button"
                  disabled={safePageNumber >= totalPages}
                  onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- helpers

function StatusBadge({ status }: { status: string }) {
  const isActive = status.toLowerCase() === "active";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-xs font-medium ring-1 ${
        isActive
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : "bg-gray-100 text-gray-600 ring-gray-200"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          isActive ? "bg-emerald-500" : "bg-gray-400"
        }`}
      />
      {status.toLowerCase()}
    </span>
  );
}
